import React from "react";
import type { ScaleLinear } from "d3";
import {
  MarkerLine,
  HorizontalMarkerLine,
  AxisIndicatorLine,
  AxisAnnotation,
} from "./SharedTooltipComponents";
import { formatYAxisValue, type ChartMargin } from "./tooltipUtils";
import type { TradeDirection, TradeFlow } from "@atlas-int/data";

export interface TooltipLineMarkerProps {
  year: number;
  x: number;
  yAxisMarkerExtent: { min: number; max: number };
  xScale?: ScaleLinear<number, number>;
  yScale?: ScaleLinear<number, number>;
  chartMargin?: ChartMargin;
  containerWidth?: number;
  containerHeight?: number;
  overTimeLayout?: string;
  tooltipData?: {
    shareForYear?: number;
    exportValue?: number;
    importValue?: number;
    value?: number;
  };
  tradeDirection?: TradeDirection;
  tradeFlow?: TradeFlow;
}

export const TooltipLineMarker: React.FC<TooltipLineMarkerProps> = ({
  year,
  x,
  yAxisMarkerExtent,
  xScale,
  yScale,
  chartMargin,
  containerWidth: _containerWidth,
  containerHeight,
  overTimeLayout,
  tooltipData,
  tradeDirection,
  tradeFlow,
}) => {
  if (!year || x === undefined || !yAxisMarkerExtent) {
    return <></>;
  }

  const margin = chartMargin || { left: 60, right: 20, top: 20, bottom: 40 };
  const cHeight = containerHeight || 600;

  const yMin = yAxisMarkerExtent.min;
  const yMax = yAxisMarkerExtent.max;

  const y1 = yMin;
  const y2 = yMax;

  const verticalLineStyle = {
    top: `${y2}px`,
    left: `${x}px`,
    height: `${y1 - y2}px`,
  };

  const xAxisAnnotationStyle = {
    top: `${cHeight - margin.bottom + 9}px`,
    left: `${x}px`,
    transform: "translateX(-50%)",
  };

  if (!xScale || !yScale) {
    return (
      <>
        <MarkerLine style={verticalLineStyle} />
        <AxisAnnotation style={xAxisAnnotationStyle}>{year}</AxisAnnotation>
      </>
    );
  }

  const yAxisPosition = margin.left;

  const topHorizontalLineStyle = {
    top: `${y2}px`,
    left: `${yAxisPosition}px`,
    width: `${x - yAxisPosition}px`,
  };

  const bottomHorizontalLineStyle = {
    top: `${y1}px`,
    left: `${yAxisPosition}px`,
    width: `${x - yAxisPosition}px`,
  };

  const xAxisLineStyle = {
    top: `${y1}px`,
    left: `${x}px`,
    height: `${Math.max(cHeight - margin.bottom - y1, 0)}px`,
  };

  const yAxisAnnotationStyle = {
    top: `${(y1 + y2) / 2 - 10}px`,
    left: `${margin.left - 10}px`,
    transform: "translateX(-100%)",
  };

  const prominentYAxisVerticalAnnotationStyle = {
    top: `${y2}px`,
    left: `${yAxisPosition}px`,
    width: "3px",
    height: `${y1 - y2}px`,
  };

  const prominentYAxisAnnotationStyle = {
    top: `${(y1 + y2) / 2 - 10}px`,
    left: `${margin.left - 10}px`,
    transform: "translateX(-100%)",
  };

  const formattedYValue = formatYAxisValue({
    tooltipData,
    overTimeLayout,
    yScale,
    y1,
    y2,
    tradeFlow,
    tradeDirection,
  });

  return (
    <>
      <MarkerLine style={verticalLineStyle} />
      <MarkerLine style={prominentYAxisVerticalAnnotationStyle} />
      <HorizontalMarkerLine style={topHorizontalLineStyle} />
      <HorizontalMarkerLine style={bottomHorizontalLineStyle} />
      <AxisIndicatorLine style={xAxisLineStyle} />
      {formattedYValue && (
        <AxisAnnotation style={yAxisAnnotationStyle}>
          {formattedYValue}
        </AxisAnnotation>
      )}
      {formattedYValue && (
        <AxisAnnotation style={prominentYAxisAnnotationStyle}>
          {formattedYValue}
        </AxisAnnotation>
      )}
      <AxisAnnotation style={xAxisAnnotationStyle}>{year}</AxisAnnotation>
    </>
  );
};
